/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Notification from "../common/Notification";
import PropTypes from "prop-types";

import {
  Button,
  IconButton,
  Grid,
  MenuItem,
  Dialog,
  InputLabel,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";

import StyledTextField from "../common/StyledTextField";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";
import types from "../../helpers/types";
import { processSpotLegClaim } from "../../slices/updateSpotLegSlice";

const RejectClaimModal = ({
  handleClose,
  open,
  claimId,
  spotLegId,
  handleNotification,
}) => {
  const dispatch = useDispatch();

  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionNote, setRejectionNote] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isPostLegBtnDisabled, setIsPostLegBtnDisabled] = useState(true);

  const isProcessingClaimRequestStatus = useSelector(
    (state) =>
      state.updateSpotLeg.processClaimRequestStatus ===
      types.requestStatuses.inProgress
  );

  const handleRejectionReasonChange = (event) => {
    const rejectionType = Number(event.target.value);
    setRejectionReason(rejectionType);
  };

  const handleRejectionNoteChange = (event) => {
    const note = event.target.value;
    setRejectionNote(note);
    if (!note) {
      setIsPostLegBtnDisabled(true);
    }
  };

  useEffect(() => {
    if (rejectionReason && rejectionNote) {
      setIsPostLegBtnDisabled(false);
    }
  });

  const handleRejectClaim = () => {
    const params = {
      accepted: false,
      rejectionReason,
      rejectionNote,
    };
    dispatch(
      processSpotLegClaim(
        claimId,
        params,
        spotLegId,
        handleNotification,
        handleClose
      )
    );
  };

  useEffect(() => {
    setRejectionNote("");
    setRejectionReason("");
  }, [open]);

  return (
    <>
      {open && (
        <Dialog
          onClose={handleClose}
          open={true}
          maxWidth="sm"
          fullWidth={true}
          disableBackdropClick={true}
          data-testid="reject-claim-modal"
        >
          <MuiDialogTitle>
            Reject Claim
            <IconButton style={{ float: "right" }} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </MuiDialogTitle>
          <MuiDialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                Why are you rejecting this carrier's claim?
              </Grid>
              <Grid item xs={6}>
                <StyledFormControl variant="outlined" fullWidth={true}>
                  <InputLabel id="user-type">Reason</InputLabel>
                  <StyledSelect
                    value={rejectionReason}
                    label="Reason"
                    labelId="rejection-reason"
                    onChange={handleRejectionReasonChange}
                    data-testid="rejection-reason-select"
                  >
                    <MenuItem value={types.rejectionReasons.legCovered}>
                      {types.rejectionReasonDescriptions.legCovered}
                    </MenuItem>
                    <MenuItem value={types.rejectionReasons.legCancelled}>
                      {types.rejectionReasonDescriptions.legCancelled}
                    </MenuItem>
                    <MenuItem value={types.rejectionReasons.proposedDateTime}>
                      {types.rejectionReasonDescriptions.proposedDateTime}
                    </MenuItem>
                    <MenuItem value={types.rejectionReasons.other}>
                      {types.rejectionReasonDescriptions.other}
                    </MenuItem>
                  </StyledSelect>
                </StyledFormControl>
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  onChange={handleRejectionNoteChange}
                  label="Reject note"
                  variant="outlined"
                  value={rejectionNote}
                  fullWidth={true}
                  inputProps={{
                    "data-testid": "rejection-note-textfield",
                    maxLength: 2048,
                  }}
                />
              </Grid>
            </Grid>
          </MuiDialogContent>
          <MuiDialogActions>
            <Button
              variant="contained"
              onClick={handleRejectClaim}
              disabled={isProcessingClaimRequestStatus || isPostLegBtnDisabled}
              data-testid="post-leg-btn"
            >
              POST LEG
            </Button>
          </MuiDialogActions>
        </Dialog>
      )}
      <Notification
        message={types.notifications.claimRejected}
        open={showSuccessNotification}
        setOpen={setShowSuccessNotification}
      />
    </>
  );
};

RejectClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  claimId: PropTypes.number.isRequired,
  spotLegId: PropTypes.number.isRequired,
  handleNotification: PropTypes.func.isRequired,
};

export default RejectClaimModal;
